import { Controller } from "@hotwired/stimulus"
import Splide from '@splidejs/splide'

/**
 * Splide Carousel Controller
 * A modern slider and carousel, using Splide.js library
 * Documentation: https://splidejs.com/
 */
export default class extends Controller {
  static values = {
    options: { type: Object, default: {} },
    autoplay: { type: Boolean, default: false },
    interval: { type: Number, default: 3000 },
    perPage: { type: Number, default: null },
    gap: { type: String, default: null },
    type: { type: String, default: "slide" },
    arrowsEnabled: { type: Boolean, default: true },
    paginationEnabled: { type: Boolean, default: true },
    rewind: { type: Boolean, default: true },
    drag: { type: Boolean, default: true },
    focus: { type: String, default: 'center' },
    waitForTransition: { type: Boolean, default: true },
    lazyLoad: { type: String, default: 'nearby' },
    perMove: { type: Number, default: 1 },
    padding: { type: String, default: null },
    trimSpace: { type: Boolean, default: true },
    pauseOnHover: { type: Boolean, default: true },
    pauseOnFocus: { type: Boolean, default: true },
    speed: { type: Number, default: 400 },
    easing: { type: String, default: 'cubic-bezier(0.25, 1, 0.5, 1)' },
    debug: { type: Boolean, default: true } // Debug mode for development
  }

  connect() {
    
    if (this.debugValue) {
      
    }

    this.defaultOptions = {
      type: this.typeValue,
      perPage: this.perPageValue || this.getResponsivePerPage(),
      perMove: this.perMoveValue,
      gap: this.gapValue || this.getResponsiveGap(),
      padding: this.paddingValue,
      focus: this.focusValue,
      rewind: this.rewindValue,
      speed: this.speedValue,
      easing: this.easingValue,
      drag: this.dragValue,
      arrows: this.arrowsEnabledValue,
      pagination: this.paginationEnabledValue,
      autoplay: this.autoplayValue,
      interval: this.intervalValue,
      pauseOnHover: this.pauseOnHoverValue,
      pauseOnFocus: this.pauseOnFocusValue,
      waitForTransition: this.waitForTransitionValue,
      lazyLoad: this.lazyLoadValue,
      trimSpace: this.trimSpaceValue,
      
      // Responsive breakpoints with defaults optimized for different devices
      breakpoints: {
        640: { // sm - mobile
          perPage: 1,
          gap: '0.5rem',
          padding: this.paddingValue || { left: '1rem', right: '1rem' },
          focus: 'center',
          arrows: window.innerWidth > 480
        },
        768: { // md - tablet
          perPage: Math.min(2, this.perPageValue || 2),
          gap: '1rem',
          padding: this.paddingValue || { left: '1rem', right: '1rem' }
        },
        1024: { // lg - desktop
          perPage: this.perPageValue || 2,
          gap: this.gapValue || '1.5rem'
        },
        1280: { // xl - large desktop
          perPage: this.perPageValue || 3,
          gap: this.gapValue || '2rem'
        }
      }
    }

    // Merge default options with any custom options passed in
    this.splideOptions = {
      ...this.defaultOptions,
      ...this.optionsValue
    }

    if (this.debugValue) {
    }

    // Initialize Splide
    this.initSplide()
  }

  disconnect() {
    if (this.debugValue) {
    }
    
    if (this.splide) {
      this.splide.destroy(true) // true completely removes all elements
      this.splide = null
    }
  }

  initSplide() {
    // Small timeout to ensure DOM is fully ready
    setTimeout(() => {
      try {
        // Check if the DOM structure is correct
        const track = this.element.querySelector('.splide__track');
        const list = track ? track.querySelector('.splide__list') : null;
        const slides = list ? list.querySelectorAll('.splide__slide') : [];
        
        if (this.debugValue) {
          
        }
        
        if (!track || !list || slides.length === 0) {
        }
      
        this.splide = new Splide(this.element, this.splideOptions);
        this.splide.mount();

        if (this.debugValue) {
        }

        // Add event listeners
        this.setupEventListeners();

        // Dispatch event when Splide is ready
        this.dispatch('ready', { 
          detail: { 
            splide: this.splide,
            options: this.splideOptions
          }
        })
      } catch (error) {
      }
    }, 100)
  }

  setupEventListeners() {
    if (!this.splide) return
    
    // Moved event - when slide changes
    this.splide.on('moved', () => {
      if (this.debugValue) {
      }
      this.dispatch('moved', { 
        detail: { 
          index: this.splide.index,
          current: this.splide.Components.Elements.slides[this.splide.index] 
        } 
      })
    })

    // Visible event - when slides become visible
    this.splide.on('visible', (slide) => {
      this.dispatch('visible', { 
        detail: { 
          slide 
        } 
      })
    })

    // Active event - when a slide becomes active
    this.splide.on('active', (slide) => {
      this.dispatch('active', { 
        detail: { 
          slide 
        } 
      })
    })

    // Drag events
    this.splide.on('drag', () => {
      this.dispatch('drag')
    })

    this.splide.on('dragged', () => {
      this.dispatch('dragged')
    })
  }

  getResponsivePerPage() {
    const width = window.innerWidth
    if (width < 640) return 1
    if (width < 1024) return 2
    return 3
  }

  getResponsiveGap() {
    const width = window.innerWidth
    if (width < 640) return '0.5rem'
    if (width < 1024) return '1rem'
    return '1.5rem'
  }

  // Public methods that can be triggered with data-action
  next() {
    if (this.splide) this.splide.go('>')
  }

  prev() {
    if (this.splide) this.splide.go('<')
  }

  goTo(event) {
    const index = event.currentTarget.dataset.index || event.params.index
    if (this.splide && index !== undefined) {
      this.splide.go(parseInt(index))
    }
  }

  // Add/remove slides dynamically (useful for AJAX loaded content)
  add(content, index) {
    if (!this.splide) return
    this.splide.add(content, index)
    this.splide.refresh()
  }

  remove(index) {
    if (!this.splide) return
    this.splide.remove(index)
    this.splide.refresh()
  }

  refresh() {
    if (this.splide) this.splide.refresh()
  }

  // Toggle autoplay
  toggleAutoplay() {
    if (!this.splide) return
    const Autoplay = this.splide.Components.Autoplay
    if (Autoplay.isPaused()) {
      Autoplay.play()
    } else {
      Autoplay.pause()
    }
  }
}
